import { useEffect, useState } from "react";
import { Mail, Send, Star, Lock, Share2, ArrowRight, X, Clock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { mediaUrl } from "@/lib/api";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/site/Reveal";

const nfmt = new Intl.NumberFormat("en-US");
const DAY = 1000 * 60 * 60 * 24;
const firstName = (name) => String(name || "").replace(/^example\s*[—–-]\s*/i, "").trim().split(/\s+/)[0] || "them";

const parseSince = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export const daysSince = (v, now = new Date()) => {
  const d = parseSince(v);
  return d ? Math.max(0, Math.floor((now - d) / DAY)) : 0;
};

const renderEmphasis = (text) =>
  String(text || "").split(/\*(.+?)\*/g).map((p, i) =>
    i % 2 === 1 ? <span key={i} className="italic gold-gradient-text">{p}</span> : <span key={i}>{p}</span>);

const defaultScript = (p) =>
  `Dear Warden and Officials,\n\nI am writing to express deep concern about the prolonged solitary confinement of ${p.name}${p.facility ? ` at ${p.facility}` : ""}. Extended isolation causes lasting psychological harm and is widely recognized as inhumane.\n\nI respectfully urge you to review ${firstName(p.name)}'s placement immediately, provide meaningful mental health care, and move ${firstName(p.name)} out of solitary confinement.\n\nSincerely,\n[Your name]\n[Your city, state]`;

const shareLink = (p) => `${window.location.origin}/#still-inside`;

const share = async (p) => {
  const text = `${p.name} is in solitary confinement right now. Learn their story and take action.`;
  const url = shareLink(p);
  if (navigator.share) {
    try { await navigator.share({ title: `Still Inside — ${p.name}`, text, url }); return; } catch { /* cancelled */ }
  }
  try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link copied — share it anywhere."); }
  catch { toast.message(url); }
};

function Actions({ p, idx, compact = false }) {
  const first = firstName(p.name);
  const script = p.contact_script || defaultScript(p);
  const linkCls = "flex items-center gap-2 text-imi-text/70 hover:text-imi-goldlight text-sm transition-colors group/link";
  return (
    <div className={compact ? "space-y-2" : "space-y-3"} data-testid={`actions-${idx}`}>
      <a href={`mailto:?subject=${encodeURIComponent(`Free ${p.name}`)}&body=${encodeURIComponent(script)}`} className={linkCls} data-testid={`contact-${idx}`}>
        <Star size={14} className="text-imi-gold shrink-0" /> <span className="underline decoration-imi-gold/30 group-hover/link:decoration-imi-goldlight">Email officials on {first}'s behalf</span>
      </a>
      {p.write_address && (
        <a href={`mailto:?subject=${encodeURIComponent(`A letter for ${p.name}`)}&body=${encodeURIComponent(`Mailing address:\n${p.write_address}\n\nA handwritten letter of solidarity means everything to someone in isolation.`)}`} className={linkCls} data-testid={`write-${idx}`}>
          <Mail size={14} className="text-imi-gold shrink-0" /> <span className="underline decoration-imi-gold/30 group-hover/link:decoration-imi-goldlight">Write to {first}</span>
        </a>
      )}
      {p.petition_url && (
        <a href={p.petition_url} target="_blank" rel="noopener noreferrer" className={linkCls} data-testid={`petition-${idx}`}>
          <Send size={14} className="text-imi-gold shrink-0" /> <span className="underline decoration-imi-gold/30 group-hover/link:decoration-imi-goldlight">Sign the petition</span>
        </a>
      )}
      <button type="button" onClick={(e) => { e.stopPropagation(); share(p); }} className={linkCls} data-testid={`share-${idx}`}>
        <Share2 size={14} className="text-imi-gold shrink-0" /> <span className="underline decoration-imi-gold/30 group-hover/link:decoration-imi-goldlight">Share {first}'s story</span>
      </button>
    </div>
  );
}

function DetailDialog({ p, idx, now, onClose }) {
  if (!p) return null;
  const img = p.photo ? mediaUrl({ media_id: p.photo }) : null;
  const days = daysSince(p.since, now);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-0 bg-imi-surface border-imi-gold/30 text-imi-text overflow-hidden [&>button]:hidden max-h-[92vh] overflow-y-auto" data-testid="still-inside-dialog">
        <div className="absolute top-3 right-3 z-20">
          <button type="button" onClick={onClose} data-testid="still-inside-dialog-close" className="h-10 w-10 inline-flex items-center justify-center bg-imi-bg/80 border border-imi-border text-imi-text hover:text-imi-gold hover:border-imi-gold transition-colors">
            <X size={18} />
          </button>
        </div>
        <div className="grid md:grid-cols-12">
          <div className="md:col-span-5 bg-imi-elevated relative min-h-[260px]">
            {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : (
              <div className="w-full h-full min-h-[260px] flex items-center justify-center"><Lock size={64} className="text-imi-gold/20" /></div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-imi-bg via-imi-bg/60 to-transparent p-5">
              <div className="font-serif text-imi-goldlight text-5xl leading-none" data-testid="dialog-days">{nfmt.format(days)}</div>
              <div className="text-imi-text/70 text-[11px] uppercase tracking-[0.25em] mt-1">{p.since ? "days in solitary — and counting" : "date of isolation not yet confirmed"}</div>
            </div>
          </div>
          <div className="md:col-span-7 p-7 md:p-9">
            <div className="text-[10px] uppercase tracking-[0.35em] text-imi-gold mb-2">Still Inside</div>
            <DialogTitle className="font-serif text-3xl md:text-4xl text-imi-text leading-tight">{p.name}</DialogTitle>
            <DialogDescription className="mt-2 text-imi-text/50 text-sm">
              {[p.facility, p.since ? `Isolated since ${parseSince(p.since)?.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}` : null].filter(Boolean).join(" · ")}
            </DialogDescription>
            {p.story && <p className="mt-6 text-imi-text/75 text-base leading-relaxed whitespace-pre-line" data-testid="dialog-story">{p.story}</p>}
            {p.write_address && (
              <div className="mt-6 border border-imi-border bg-imi-bg/60 p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-imi-gold mb-2">Mailing address</div>
                <pre className="font-sans text-imi-text/80 text-sm whitespace-pre-wrap" data-testid="dialog-address">{p.write_address}</pre>
              </div>
            )}
            <div className="mt-7 pt-6 border-t border-imi-border">
              <div className="text-[10px] uppercase tracking-[0.25em] text-imi-gold mb-3">Take action</div>
              <Actions p={p} idx={`dialog-${idx}`} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StillInside({ settings }) {
  const list = (settings?.still_inside || []).filter((p) => p && (p.name || "").trim());
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);
  if (list.length === 0) return null;
  const totalDays = list.reduce((sum, p) => sum + daysSince(p.since, now), 0);
  const totalYears = (totalDays / 365).toFixed(1);
  return (
    <section id="still-inside" className="relative border-b border-imi-border py-16 md:py-20 bg-imi-bg" data-testid="still-inside-section">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 60px, #d4af5a 60px, #d4af5a 61px)" }} />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <Reveal className="max-w-3xl mb-8 md:mb-10">
          <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-4">{settings?.still_inside_kicker || "Still Inside"}</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
            {renderEmphasis(settings?.still_inside_title || "The people we *refuse to forget*.")}
          </h2>
          {settings?.still_inside_subtitle && (
            <p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed">{settings.still_inside_subtitle}</p>
          )}
        </Reveal>

        <Reveal className="mb-8 md:mb-10">
          <div className="border border-imi-gold/30 bg-imi-elevated px-5 py-4 md:px-7 md:py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" data-testid="free-them-tally">
            <div className="flex items-baseline gap-3 shrink-0">
              <span className="text-[10px] uppercase tracking-[0.35em] text-imi-gold">Free Them</span>
              <span className="font-serif text-imi-goldlight text-3xl md:text-4xl leading-none tracking-tight" data-testid="free-them-count">{nfmt.format(totalDays)}</span>
              <span className="text-imi-text/50 text-xs">days · {totalYears} yrs combined</span>
            </div>
            <div className="hidden sm:block h-6 w-px bg-imi-gold/30" />
            <div className="text-imi-text/60 text-xs md:text-sm leading-snug flex items-center gap-2">
              <Clock size={12} className="text-imi-gold shrink-0" />
              Live count for the {list.length} {list.length === 1 ? "person" : "people"} below, as of {now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}. Tap a name to read their story and take action.
            </div>
          </div>
        </Reveal>

        <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {list.map((p, i) => {
            const img = p.photo ? mediaUrl({ media_id: p.photo }) : null;
            const days = daysSince(p.since, now);
            const sinceDate = parseSince(p.since);
            return (
              <StaggerItem key={i}>
                <article
                  role="button" tabIndex={0}
                  onClick={() => setOpen(i)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(i); } }}
                  className="group h-full border border-imi-border bg-imi-surface/50 hover:border-imi-gold/60 hover:bg-imi-surface transition-colors flex gap-4 p-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-imi-gold"
                  data-testid={`still-inside-${i}`}
                >
                  <div className="w-[88px] md:w-[96px] shrink-0 aspect-[3/4] bg-imi-elevated relative overflow-hidden">
                    {img ? (
                      <img src={img} alt={p.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Lock size={28} className="text-imi-gold/25" /></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 flex flex-col">
                    <div className="font-serif text-imi-text text-lg leading-tight line-clamp-2 group-hover:text-imi-goldlight transition-colors">{p.name}</div>
                    {p.facility && <div className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-imi-gold/80 truncate">{p.facility}</div>}
                    <div className="mt-2 flex items-baseline gap-1.5">
                      {sinceDate ? (
                        <>
                          <span className="font-serif text-imi-goldlight text-2xl leading-none" data-testid={`days-${i}`}>{nfmt.format(days)}</span>
                          <span className="text-imi-text/50 text-[10px] uppercase tracking-[0.18em]">days in solitary</span>
                        </>
                      ) : (
                        <span className="text-imi-text/50 text-[10px] uppercase tracking-[0.18em]" data-testid={`days-${i}`}>In solitary · date pending</span>
                      )}
                    </div>
                    {p.story && <p className="mt-2 text-imi-text/55 text-xs leading-snug line-clamp-2">{p.story}</p>}
                    <div className="mt-auto pt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-imi-gold group-hover:text-imi-goldlight transition-colors" data-testid={`learn-more-${i}`}>
                      <span className="whitespace-nowrap">Learn more &amp; act</span> <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
      {open !== null && <DetailDialog p={list[open]} idx={open} now={now} onClose={() => setOpen(null)} />}
    </section>
  );
}
